/**
 * Utility functions to more easily interpret board settings, relationships, and access permissions between objects
 *
 * Copyright (c) 2019 Propellant Software Corp
 * Author: Kelvin Nguyen
 */
import boardRoles from '../../enums/boardRoles'

export const Roles = boardRoles

/**
 * Validates whether the current user is the owner of the provided board
 * @param {Object} user The user domain model
 * @param {Object} board The board domain model
 */
export const isBoardOwner = (user = {}, board = {}) => {
  const { owner = {} } = board
  const { uid } = user

  return (owner.uid === uid)
}

/**
 * Get the collaborator that matches the current user for the provided board
 * @param {Object} user The user domain model
 * @param {Object} board The board domain model
 */
export const getBoardCollaborator = (user = {}, board = {}) => {
  const { collaborators = [] } = board
  const { uid } = user

  return collaborators.find(collaborator => collaborator.uid === uid) || {}
}

/**
 * Validates whether the current user is a collaborator of the provided board
 * @param {Object} user The user domain model
 * @param {Object} board The board domain model
 */
export const isBoardCollaborator = (user = {}, board = {}) => {
  return !!(getBoardCollaborator(user, board).role)
}

/**
 * Validates whether the current user is one of the valid roles of the provided board
 * @param {Object} user The user domain model
 * @param {Object} board The board domain model
 * @param {Object} roles The list of board roles to validate against
 */
export const isBoardRoles = (user = {}, board = {}, roles = []) => {
  const foundCollaborator = getBoardCollaborator(user, board)

  return (foundCollaborator && (roles.indexOf(foundCollaborator.role) > -1))
}

/**
 * Validates whether the current user is a follower of the provided board
 * @param {Object} user The user domain model
 * @param {Object} board The board domain model
 */
export const isFollowingBoard = (user = {}, board = {}) => {
  const { follows = {} } = board
  const followers = follows.followers || {}
  const { uid } = user

  return !!followers[uid]
}

/**
 * Returns whether board is private
 * @param {Object} board The board domain model
 */
export const isPrivateBoard = (board = {}) => {
  return (board.settings) ? (!!board.settings.isPrivate) : false
}

/**
 * Returns an object that has all the access levels for the current user
 * @param {Object} user The user domain model
 * @param {Object} board The board domain model
 */
export const getAllAccessLevels = (user, board) => {
  return {
    isOwner: isBoardOwner(user, board),
    isOwnerOrAdmin: isBoardRoles(user, board, [Roles.owner, Roles.admin]),
    isCollaborator: isBoardCollaborator(user, board),
    isEditableCollaborator: isBoardRoles(user, board, [Roles.owner, Roles.admin, Roles.editor]),
    isFollowing: isFollowingBoard(user, board),
    isPrivate: isPrivateBoard(board)
  }
}
